import { useEffect, useState } from "react";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Shield, Users, Play, Loader2, ExternalLink, Store, Package, CheckCircle2, LogOut, User as UserIcon, Gift, X,
} from "lucide-react";

export default function AdminUsers() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const loadUsers = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      toast.error(
        "Falha ao carregar usuários. " + (e.response?.data?.detail || "Acesso restrito a administradores.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // iter 56 — grant a courtesy Agências plan for N days
  const grantCourtesy = async (target) => {
    const daysStr = window.prompt(
      `Dar cortesia do plano AGÊNCIAS (10.000 produtos/mês) para ${target.email}?\n\nInforme por quantos DIAS (ex: 7, 15, 30). Ao expirar, o usuário volta ao estado anterior automaticamente:`,
      "7"
    );
    if (daysStr === null) return;
    const days = parseInt(daysStr, 10);
    if (!days || days < 1 || days > 365) {
      toast.error("Informe um número de dias entre 1 e 365");
      return;
    }
    try {
      const { data } = await api.post(`/admin/users/${target.id}/courtesy`, { days, tier: "agencias" });
      toast.success(`Cortesia Agências por ${data.days} dia(s) para ${data.email} — expira em ${new Date(data.expires_at).toLocaleDateString("pt-BR")}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao conceder cortesia");
    }
  };

  const revokeCourtesy = async (target) => {
    if (!window.confirm(`Revogar a cortesia de ${target.email} agora? O usuário volta ao estado anterior imediatamente.`)) return;
    try {
      const { data } = await api.delete(`/admin/users/${target.id}/courtesy`);
      toast.success(`Cortesia revogada — status restaurado: ${data.restored_status}`);
      loadUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao revogar cortesia");
    }
  };

  const startImpersonation = (target) => {
    if (!window.confirm(`Atuar como ${target.email}?\n\nTodas as suas ações passarão a agir na conta deste usuário até você sair da atuação.`)) return;
    try {
      localStorage.setItem("impersonate_user_id", target.id);
      localStorage.setItem("impersonate_user_email", target.email);
    } catch (_) {}
    toast.success(`Atuando como ${target.email}. Recarregando...`);
    setTimeout(() => window.location.reload(), 500);
  };

  const filtered = users.filter((u) =>
    !q ||
    (u.email || "").toLowerCase().includes(q.toLowerCase()) ||
    (u.name || "").toLowerCase().includes(q.toLowerCase())
  );

  if (!user?.is_superadmin) {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
        <Sidebar />
        <main className="flex-1 p-8">
          <div className="max-w-lg border border-red-200 bg-red-50 rounded-md p-6 text-red-900">
            <Shield className="w-6 h-6 mb-2" />
            <div className="font-display font-bold text-lg mb-1">Acesso restrito</div>
            <p className="text-sm">Esta área é exclusiva de super-administradores.</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F8F9FA]">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 sm:p-10">
        <header className="mb-8">
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#002FA7]" /> Super-Admin
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#09090B] flex items-center gap-3">
            <Users className="w-8 h-8 text-[#002FA7]" />
            Todos os usuários
          </h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Lista de todas as contas cadastradas. Clique em <strong>"Atuar como"</strong> para
            entrar na sessão de um cliente e publicar em nome dele — as ações ficam registradas
            no log de auditoria.
          </p>
        </header>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Filtrar por email ou nome..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full max-w-md h-10 rounded-md border border-zinc-300 px-3 text-sm"
            data-testid="admin-users-search"
          />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando usuários...
          </div>
        ) : (
          <div className="border border-zinc-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
                <tr>
                  <th className="text-left px-4 py-2">Usuário</th>
                  <th className="text-left px-4 py-2">Assinatura</th>
                  <th className="text-left px-4 py-2">ERPs</th>
                  <th className="text-left px-4 py-2">Publicados</th>
                  <th className="text-right px-4 py-2">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200">
                {filtered.map((u) => (
                  <tr key={u.id} className={u.is_superadmin ? "bg-amber-50" : ""} data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[#09090B]">{u.email}</div>
                      <div className="text-xs text-zinc-500">{u.name || "—"}</div>
                      {u.is_superadmin && (
                        <span className="inline-block mt-1 text-[10px] font-mono uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                          Super-Admin
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs font-mono uppercase tracking-widest">
                        {u.subscription_status || "none"}
                      </div>
                      {u.subscription_tier && <div className="text-xs text-zinc-500">{u.subscription_tier}</div>}
                      {u.courtesy && (
                        <span
                          className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono uppercase tracking-widest bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded"
                          title={`Cortesia expira em ${u.courtesy_expires_at ? new Date(u.courtesy_expires_at).toLocaleString("pt-BR") : "?"}`}
                          data-testid={`courtesy-badge-${u.id}`}
                        >
                          <Gift className="w-3 h-3" />
                          Cortesia até {u.courtesy_expires_at ? new Date(u.courtesy_expires_at).toLocaleDateString("pt-BR") : "?"}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-mono">
                        <Store className="w-3 h-3" /> {u.erp_count}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs font-mono">
                        <Package className="w-3 h-3" /> {u.published_count}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        {u.courtesy ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => revokeCourtesy(u)}
                            className="rounded-md border-red-300 text-red-700 hover:bg-red-50 text-xs h-8"
                            data-testid={`admin-revoke-courtesy-${u.id}`}
                          >
                            <X className="w-3 h-3 mr-1" /> Revogar cortesia
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => grantCourtesy(u)}
                            className="rounded-md border-emerald-400 text-emerald-800 hover:bg-emerald-50 text-xs h-8"
                            data-testid={`admin-courtesy-${u.id}`}
                            title="Dar cortesia do plano Agências por X dias"
                          >
                            <Gift className="w-3 h-3 mr-1" /> Cortesia
                          </Button>
                        )}
                        {u.id === user.id ? (
                          <span className="text-[10px] font-mono text-zinc-400 uppercase">(você)</span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => startImpersonation(u)}
                            className="rounded-md brand-primary text-white text-xs h-8"
                            data-testid={`admin-actuate-${u.id}`}
                          >
                            <Play className="w-3 h-3 mr-1" /> Atuar como
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && (
              <div className="text-center text-sm text-zinc-500 py-8">Nenhum usuário encontrado</div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
